/*  src/pages/GatekeeperDashboard.jsx  */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import API from '../services/api';
import { LogOut, LogIn, Menu } from 'lucide-react';
import '../styles/Dashboard.css';

/* ---------------------------------------------------------------- helpers */
const PAGE_OPTS = [50, 100, 200];   /* rows-per-page selector */
const btn = bg => ({
  display:'flex', alignItems:'center', gap:4,
  padding:'6px 16px',
  border:'none', borderRadius:6,
  fontSize:'0.85rem', cursor:'pointer',
  color:'#fff', background:bg,
});
const fmt = (v) => (v === undefined || v === null || v === '' ? '—' : v);

/* ============================================================== component */
export default function GatekeeperDashboard() {
  /* ───────── router state */
  const [tab,      setTab]      = useState('leave');   // leave | guest | guestPrev
  const [menuOpen, setMenuOpen] = useState(false);

  /* ───────── master data pulls */
  const [reqList,   setReqList]   = useState([]);
  const [guestList, setGuestList] = useState([]);

  const fetchReqs   = useCallback(() =>
    API.get('/requests/gatekeeper').then(r => setReqList(r.data)), []);
  const fetchGuests = useCallback(() =>
    API.get('/guests').then(r => setGuestList(r.data)), []);

  useEffect(() => { fetchReqs(); fetchGuests(); }, [fetchReqs, fetchGuests]);

  /* ───────── global UI state */
  const [search, setSearch] = useState('');
  const [page,   setPage]   = useState(1);
  const [pageSz, setPageSz] = useState(PAGE_OPTS[0]);  // 50 | 100 | 200

  /* extra leave-date filters */
  const [period,  setPeriod]  = useState('all');       // today | week | month | all
  const [selDate, setSelDate] = useState('');

  /* date helpers */
  const today    = new Date(new Date().setHours(0,0,0,0));
  const tomorrow = new Date(today);  tomorrow.setDate(tomorrow.getDate()+1);
  const weekAgo  = new Date(today);  weekAgo.setDate(weekAgo.getDate()-7);
  const monthAgo = new Date(today);  monthAgo.setMonth(monthAgo.getMonth()-1);

  /* ── LEAVE: search first, date filter only when no search ── */
  const leaveRows = useMemo(() => {
    let arr = [...reqList];

    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter(r =>
        [r.name, r.studentId, r.pickUpWith, r.vehicleNo, r.leaveFor]
          .some(f => f?.toLowerCase().includes(q)));
      return arr.sort((a,b)=>new Date(b.dateTime)-new Date(a.dateTime));
    }

    if (selDate) {
      const d0 = new Date(selDate);
      const d1 = new Date(d0); d1.setDate(d1.getDate()+1);
      arr = arr.filter(r => {
        const d = new Date(r.dateTime);
        return d >= d0 && d < d1;
      });
    } else if (period !== 'all') {
      const keep = d => (
        period==='today' ? d>=today && d<tomorrow :
        period==='week'  ? d>=weekAgo :
                           d>=monthAgo
      );
      arr = arr.filter(r => keep(new Date(r.dateTime)));
    }

    return arr.sort((a,b)=>new Date(b.dateTime)-new Date(a.dateTime));
  }, [reqList, search, period, selDate]);

  /* ── GUEST lists (live / prev) ── */
  const guestLive = guestList.filter(g => g.status==='pending' || g.status==='in');
  const guestPrev = guestList.filter(g => g.status==='out');

  const guestRows = useMemo(() => {
    const base = tab==='guest' ? guestLive : guestPrev;
    const q    = search.toLowerCase();
    return base
      .filter(g =>
        [g.name, g.cnic, g.visitingOffice, g.vehicleNo]
          .some(f => f?.toLowerCase().includes(q)))
      .sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt));
  }, [tab, guestLive, guestPrev, search]);

  /* ── pagination slice ── */
  const rows       = tab==='leave' ? leaveRows : guestRows;
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSz));
  const pageRows   = rows.slice((page-1)*pageSz, page*pageSz);

  /* leave counters */
  const goingCnt = reqList.filter(r=>r.status==='approved').length;
  const outCnt   = reqList.filter(r=>r.status==='out').length;
  const inCnt    = reqList.filter(r=>r.status==='in').length;

  /* ───────── actions (status-change) */
  const [loadingLeave, setLoadingLeave] = useState({});
  const [loadingGuest, setLoadingGuest] = useState({});

  const markLeaveStatus = async (id,next)=>{
    setLoadingLeave(prev => ({...prev, [id]: true}));
    try {
      await API.put(`/requests/gatekeeper/${id}`,{status:next});
      fetchReqs();
    } catch(err) {
      alert(err.response?.data?.msg || 'Failed to update status');
    } finally {
      setLoadingLeave(prev => ({...prev, [id]: false}));
    }
  };
  const markGuestStatus = async(id,next)=>{
    setLoadingGuest(prev => ({...prev, [id]: true}));
    try {
      await API.put(`/guests/${id}`,{status:next});
      fetchGuests();
    } catch(err) {
      alert(err.response?.data?.msg || 'Failed to update status');
    } finally {
      setLoadingGuest(prev => ({...prev, [id]: false}));
    }
  };

  /* ───────── guest-creation modal */
  const [showGuestModal,setShowGuestModal] = useState(false);
  const [guestForm,setGuestForm] = useState({name:'',cnic:'',visitingOffice:'',vehicleNo:''});
  const resetGuestForm = ()=> setGuestForm({name:'',cnic:'',visitingOffice:'',vehicleNo:''});

  const saveGuest = async e=>{
    e.preventDefault();
    if(!/^\d{13}$/.test(guestForm.cnic)) return alert('CNIC must be 13 digits');
    await API.post('/guests',{...guestForm,vehicleNo:guestForm.vehicleNo||'-'});
    resetGuestForm(); setShowGuestModal(false); fetchGuests();
  };

  /* ───────── CSV helper (used in header) */
  const exportGuestsCsv = mode=>{
    const list = mode==='guest'? guestLive : guestPrev;
    if(!list.length){ alert('No guest records to export.'); return; }

    const csv = [
      ['CreatedAt','Name','CNIC','Office','Vehicle','Status','CreatedBy','InTime','OutTime'].join(','),
      ...list.map(g=>[
        new Date(g.createdAt).toLocaleString(),
        `"${(g.name||'').replace(/"/g,'""')}"`,
        g.cnic || '',
        `"${(g.visitingOffice||'').replace(/"/g,'""')}"`,
        g.vehicleNo || '',
        g.status || '',
        g.gatekeeper?.role==='vc-office'?'VC Office':'Gate-Keeper',
        g.inAt  ? new Date(g.inAt ).toLocaleString() : '-',
        g.outAt ? new Date(g.outAt).toLocaleString() : '-',
      ].join(',')),
    ].join('\n');

    const blob=new Blob([csv],{type:'text/csv;charset=utf-8'});
    const url = URL.createObjectURL(blob);
    const a=document.createElement('a');
    a.href=url; a.download=`guests_${mode}_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  /* ───────── details modal (leave row) */
  const [viewRow, setViewRow] = useState(null);

  /* ======================================================= render */
  return (
    <div className="gk-wrapper">

      {/* ======= sidebar ======= */}
      <aside className={`gk-sidebar ${menuOpen?'open':''}`}>
        <h3>🚪 GATE KEEPER</h3>

        <span className="gk-title">Leave</span>
        <button className={tab==='leave'?'active':''}
          onClick={()=>{setTab('leave');setPage(1);setSearch('');setMenuOpen(false);}}>
          Students Leave
        </button>

        <span className="gk-title">Guests</span>
        <button className={tab==='guest'?'active':''}
          onClick={()=>{setTab('guest');setPage(1);setSearch('');setMenuOpen(false);}}>
          Current Guests
        </button>
        <button className={tab==='guestPrev'?'active':''}
          onClick={()=>{setTab('guestPrev');setPage(1);setSearch('');setMenuOpen(false);}}>
          Previous Guests
        </button>
      </aside>

      {/* ======= burger (mobile) ======= */}
      <button className="gk-hamburger" onClick={()=>setMenuOpen(o=>!o)} aria-label="menu">
        <Menu size={22}/>
      </button>

      {/* ======= main ======= */}
      <main className="gk-main">

        {/* ---------- header ---------- */}
        <div className="dashboard-header">
          <h2>{{
            leave:'Students Leave-Requests',
            guest:'Current Guests',
            guestPrev:'Previous Guests'}[tab]}</h2>

          {tab==='leave' && (
            <div className="counters">
              <div className="counter-item grey"  ><div>Going Out</div><div>{goingCnt}</div></div>
              <div className="counter-item yellow"><div>Out</div><div>{outCnt}</div></div>
              <div className="counter-item green" ><div>In</div><div>{inCnt}</div></div>
            </div>
          )}

          {tab!=='leave' && (
            <div className="gk-btn-box">
              {tab==='guest' && (
                <button className="action-btn blue" onClick={()=>setShowGuestModal(true)}>
                  + New Guest
                </button>
              )}
              <button className="action-btn green" onClick={()=>exportGuestsCsv(tab)}>
                ⬇︎ Export CSV
              </button>
            </div>
          )}
        </div>

        {/* ---------- quick-range for leave ---------- */}
        {tab==='leave' && (
          <div className="quick-range">
            {['today','week','month','all'].map(r=>(
              <button key={r}
                className={period===r?'active period-btn':'period-btn'}
                onClick={()=>{setPeriod(r);setSelDate('');setPage(1);}}>
                {{today:'Today',week:'Week',month:'Month',all:'All'}[r]}
              </button>
            ))}
            <input type="date"
              value={selDate}
              onChange={e=>{setSelDate(e.target.value);setPage(1);}}
              style={{padding:'6px 10px',border:'1px solid #cbd5e1',
                      borderRadius:6,marginLeft:8}}/>
          </div>
        )}

        {/* ---------- global search ---------- */}
        <div className="search-filter" style={{marginTop:tab==='leave'?8:0}}>
          <input placeholder="Search…"
            value={search}
            onChange={e=>{setSearch(e.target.value);setPage(1);}}/>
        </div>

        {/* ===================== TABLES ===================== */}
        {tab==='leave' ? (
          /* ----- leave table ----- */
          <div className="table-container prettified">
            <table>
              <thead>
                <tr>
                  <th>Date&nbsp;&nbsp;Time</th>
                  <th>Student</th>
                  <th>ID</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Details</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length ? pageRows.map(r=>(
                  <tr key={r._id}>
                    <td>{new Date(r.dateTime).toLocaleString()}</td>
                    <td>{r.name}</td>
                    <td>{r.studentId}</td>
                    <td style={{maxWidth:220,wordBreak:'break-word'}}>
                      {fmt(r.leaveFor)}
                    </td>
                    <td><span className={`status-badge status-${r.status}`}>{r.status}</span></td>

                    {/* View details */}
                    <td>
                      <button className="view-btn" onClick={()=>setViewRow(r)}>
                        View
                      </button>
                    </td>

                    <td>
                      {r.status==='approved' &&
                        <button style={btn('#f97316')}
                          onClick={()=>markLeaveStatus(r._id,'out')}
                          disabled={loadingLeave[r._id]}>
                          {loadingLeave[r._id] ? '...' : <><LogOut size={14}/> OUT</>}
                        </button>}
                      {r.status==='out' &&
                        <button style={btn('#059669')}
                          onClick={()=>markLeaveStatus(r._id,'in')}
                          disabled={loadingLeave[r._id]}>
                          {loadingLeave[r._id] ? '...' : <><LogIn size={14}/> IN</>}
                        </button>}
                    </td>
                  </tr>
                )) : <tr><td colSpan={7} className="no-data">No records</td></tr>}
              </tbody>
            </table>
          </div>
        ) : (
          /* ----- guest tables ----- */
          <div className="table-container prettified">
            <table>
              <thead>
                <tr>
                  <th style={{width:150}}>Created</th>
                  <th>Name</th><th>CNIC</th><th>Office</th><th>Vehicle</th>
                  <th>Created&nbsp;By</th>
                  {tab==='guestPrev' && (<><th>IN&nbsp;Time</th><th>OUT&nbsp;Time</th></>)}
                  <th>Status</th>
                  {tab==='guest' && <th style={{width:90}}>Action</th>}
                </tr>
              </thead>
              <tbody>
                {pageRows.length ? pageRows.map(g=>(
                  <tr key={g._id}>
                    <td>{new Date(g.createdAt).toLocaleString()}</td>
                    <td>{g.name}</td>
                    <td>{g.cnic}</td>
                    <td>{g.visitingOffice}</td>
                    <td>{g.vehicleNo}</td>
                    <td>{g.gatekeeper?.role==='vc-office'?'VC Office':'Gate-Keeper'}</td>

                    {tab==='guestPrev' && (
                      <>
                        <td>{g.inAt  ? new Date(g.inAt ).toLocaleString() : '-'}</td>
                        <td>{g.outAt ? new Date(g.outAt).toLocaleString() : '-'}</td>
                      </>
                    )}

                    <td><span className={`status-badge status-${g.status}`}>{g.status}</span></td>

                    {tab==='guest' && (
                      <td className="actions">
                        {g.status==='pending' && (
                          <button style={btn('#059669')}
                            onClick={()=>markGuestStatus(g._id,'in')}
                            disabled={loadingGuest[g._id]}>
                            {loadingGuest[g._id] ? '...' : <><LogIn size={14}/> IN</>}
                          </button>)}
                        {g.status==='in' && (
                          <button style={btn('#f97316')}
                            onClick={()=>markGuestStatus(g._id,'out')}
                            disabled={loadingGuest[g._id]}>
                            {loadingGuest[g._id] ? '...' : <><LogOut size={14}/> OUT</>}
                          </button>)}
                      </td>
                    )}
                  </tr>
                )) : (
                  <tr><td colSpan={tab==='guest'?9:9} className="no-data">No records</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* ---------- pagination + page-size ---------- */}
        <div className="pagination">
          <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}>Prev</button>
          <span className="page-info">Page {page} of {totalPages}</span>
          <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}>Next</button>

          <select value={pageSz}
            onChange={e=>{setPageSz(Number(e.target.value));setPage(1);}}
            style={{marginLeft:12}}>
            {PAGE_OPTS.map(n=> <option key={n} value={n}>{n}/page</option>)}
          </select>
        </div>

        {/* ---------- create-guest modal ---------- */}
        {showGuestModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header"><h3>New Guest</h3></div>

              <form className="modal-body" onSubmit={saveGuest}>
                {['name','cnic','visitingOffice','vehicleNo'].map(k=>(
                  <div className="form-group" key={k}>
                    <label>{
                      { name:'Name', cnic:'CNIC (13 digits)',
                        visitingOffice:'Visiting Office',
                        vehicleNo:'Vehicle No. (optional)' }[k]
                    }</label>
                    <input
                      value={guestForm[k]}
                      onChange={e=>setGuestForm({...guestForm,[k]:e.target.value})}
                      {...(k==='cnic' && {pattern:'\\d{13}', title:'13-digit CNIC required'})}
                      {...(k!=='vehicleNo' && {required:true})}
                    />
                  </div>
                ))}

                <div className="modal-footer">
                  <button type="submit">Save</button>
                  <button type="button" className="cancel-btn-form"
                    onClick={()=>{setShowGuestModal(false);resetGuestForm();}}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ---------- details modal (sleek) ---------- */}
        {viewRow && (
          <div className="modal-overlay details-modal">
            <div className="modal sleek">
              <div className="modal-header details-header">
                <h3>Leave Request Details</h3>
                <button
                  className="icon-close"
                  onClick={() => setViewRow(null)}
                  aria-label="Close"
                  title="Close"
                >
                  ×
                </button>
              </div>

              {/* Top summary strip */}
              <div className="summary-strip">
                <div className="summary-left">
                  <div className="summary-title">
                    <span className="student-name">{fmt(viewRow.name)}</span>
                    <span className="chip-id">ID: {fmt(viewRow.studentId)}</span>
                  </div>
                  <div className="summary-sub">
                    {fmt(viewRow.email)}
                    {viewRow.hostelType ? (
                      <span className="chip-hostel">{viewRow.hostelType}</span>
                    ) : null}
                  </div>
                </div>

                <div className="summary-right">
                  <span className={`status-badge status-${viewRow.status}`}>{viewRow.status}</span>
                </div>
              </div>

              <div className="modal-body">
                {/* Key–Value grid */}
                <div className="kv-grid">
                  <div className="kv">
                    <div className="k">Reason</div>
                    <div className="v prewrap">{fmt(viewRow.leaveFor)}</div>
                  </div>

                  <div className="kv">
                    <div className="k">Leaving With</div>
                    <div className="v">{fmt(viewRow.pickUpWith)}</div>
                  </div>

                  <div className="kv">
                    <div className="k">Transport</div>
                    <div className="v">{fmt(viewRow.transport)}</div>
                  </div>

                  <div className="kv">
                    <div className="k">Vehicle No</div>
                    <div className="v">{fmt(viewRow.vehicleNo)}</div>
                  </div>

                  <div className="kv">
                    <div className="k">Driver Name</div>
                    <div className="v">{fmt(viewRow.driverName)}</div>
                  </div>

                  <div className="kv">
                    <div className="k">Date &amp; Time</div>
                    <div className="v mono">
                      {new Date(viewRow.dateTime).toLocaleString()}
                    </div>
                  </div>

                  <div className="kv full">
                    <div className="k">Warden Comment</div>
                    <div className="v note prewrap">{fmt(viewRow.wardenComment)}</div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button className="close-btn" onClick={() => setViewRow(null)}>Close</button>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
